/**
 * SearchBox — the one search box (FR-SRC-01/03/07). Two sizes: `bar` (top bar) and
 * `hero` (home). A "Search tips" popover lists the query syntax; obvious mistakes
 * (unclosed quote, unknown field) are explained under the box before sending. An
 * optional scope chip limits the search to a cabinet/folder and can be removed.
 *
 * The "/" shortcut is global (AppShell) and focuses the element with id SEARCH_INPUT_ID.
 */
import { CircleHelp, Search, X } from 'lucide-react';
import { forwardRef, useId, useState, type FormEvent } from 'react';
import { checkQuery, SYNTAX } from '../../lib/query';
import { cx, Kbd } from './core';
import { Popover } from './popover';

export const SEARCH_INPUT_ID = 'global-search';

export interface SearchScope {
  label: string;
  onClear: () => void;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (q: string) => void;
  size?: 'bar' | 'hero';
  scope?: SearchScope | null;
  id?: string;
  autoFocus?: boolean;
  placeholder?: string;
  /** Show the "/" key hint inside the box. */
  shortcutHint?: boolean;
  className?: string;
}

export const SearchBox = forwardRef<HTMLInputElement, Props>(function SearchBox({ value, onChange, onSubmit, size = 'bar', scope, id, autoFocus, placeholder = 'Search every document — words, names, numbers', shortcutHint, className }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [tried, setTried] = useState(false);
  const problem = tried ? checkQuery(value) : null;
  const hero = size === 'hero';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    const q = value.trim();
    if (!q || checkQuery(q)) return;
    onSubmit(q);
  };
  return (
    <form role="search" onSubmit={submit} className={cx('min-w-0', className)} aria-label="Search documents">
      <div className={cx('flex items-center gap-1 rounded-tag border bg-card transition-colors focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20', problem ? 'border-danger' : 'border-strong', hero ? 'min-h-[60px] pl-4 pr-1.5' : 'min-h-touch pl-3 pr-1')}>
        <Search aria-hidden className={cx('shrink-0 text-muted', hero ? 'h-5 w-5' : 'h-4 w-4')} />
        {scope && (
          <span className="inline-flex h-8 max-w-[40%] shrink-0 items-center gap-1 rounded-[4px] bg-manila-soft pl-2 text-xs font-semibold text-manila">
            <span className="truncate">In {scope.label}</span>
            <button type="button" onClick={scope.onClear} aria-label={`Search everywhere, not only in ${scope.label}`} className="grid h-8 w-7 place-items-center rounded-[4px] hover:bg-manila/15">
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
        <label htmlFor={inputId} className="sr-only">
          Search documents
        </label>
        <input
          ref={ref}
          id={inputId}
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          value={value}
          placeholder={placeholder}
          aria-invalid={!!problem || undefined}
          aria-describedby={problem ? `${inputId}-problem` : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            if (tried) setTried(false);
          }}
          className={cx('min-w-0 flex-1 bg-transparent px-1.5 text-ink outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:hidden', hero ? 'h-14 text-lg' : 'h-10 text-base')}
        />
        {value && (
          <button type="button" aria-label="Clear search" onClick={() => onChange('')} className="grid h-10 w-10 shrink-0 place-items-center rounded-tag text-muted hover:text-ink">
            <X aria-hidden className="h-4 w-4" />
          </button>
        )}
        {shortcutHint && !value && (
          <span className="hidden shrink-0 lg:inline" aria-hidden>
            <Kbd>/</Kbd>
          </span>
        )}
        <SyntaxHelp onPick={(ex) => onChange(value ? `${value} ${ex}` : ex)} />
        {hero && (
          <button type="submit" className="ml-1 hidden min-h-touch shrink-0 sm:block rounded-tag bg-brand px-5 text-sm font-semibold text-brand-ink hover:bg-brand/90">
            Search
          </button>
        )}
      </div>
      {problem && (
        <p id={`${inputId}-problem`} role="alert" className="mt-1.5 text-sm text-danger">
          {problem}
        </p>
      )}
    </form>
  );
});

function SyntaxHelp({ onPick }: { onPick: (example: string) => void }) {
  return (
    <Popover
      align="right"
      label="Search tips"
      className="w-[min(26rem,calc(100vw-2rem))]"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} aria-label="Search tips" title="Search tips" className="grid h-10 w-10 shrink-0 place-items-center rounded-tag text-muted hover:bg-sunken hover:text-ink">
          <CircleHelp aria-hidden className="h-4 w-4" />
        </button>
      )}
    >
      {(close) => (
        <div className="p-2">
          <p className="text-sm font-semibold">Search tips</p>
          <p className="mb-2 text-xs text-muted">Pick an example to add it to your search.</p>
          <ul className="ledger rounded-[4px] border border-line">
            {SYNTAX.map((s) => (
              <li key={s.example}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onPick(s.example);
                    close();
                  }}
                  className="flex min-h-touch w-full flex-col items-start gap-0.5 px-2.5 py-1.5 text-left hover:bg-sunken sm:flex-row sm:items-baseline sm:gap-3"
                >
                  <span className="code shrink-0 text-sm text-ink sm:w-44">{s.example}</span>
                  <span className="text-xs text-muted">{s.meaning}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Popover>
  );
}
